import { createBrowserRouter } from 'react-router-dom';
import { sdk } from '@mesa-labs/mesa-api';
import { lazyWithRetry } from '@mesa-labs/utils/lazyWithRetry';
import AuthGuard from './components/auth/AuthGuard';
import PermissionGuard from './components/auth/PermissionGuard';
import RoleGuard from './components/auth/RoleGuard';
import DashboardLayout from './components/layout/DashboardLayout';
import {
  DemoPage,
  EmbedSettingsPage,
  HelpPage,
  LaunchStatusPage,
  LogsPage,
  StatsPage,
  VendorsPage,
} from './pages/stubs';
import { AuthGroup } from './auth/groups';

const SignIn = lazyWithRetry('sign-in', () => import('./pages/SignIn'));
const SignInImpersonate = lazyWithRetry('sign-in-impersonate', () => import('./pages/SignInImpersonate'));
const ForgotPassword = lazyWithRetry('forgot-password', () => import('./pages/ForgotPassword'));
const SignUp = lazyWithRetry('sign-up', () => import('./pages/SignUp'));
const Unauthorized = lazyWithRetry('unauthorized', () => import('./pages/Unauthorized'));
const Overview = lazyWithRetry('overview', () => import('./pages/Overview'));
const Clients = lazyWithRetry('clients', () => import('./pages/Clients'));
const ClientDetail = lazyWithRetry('client-detail', () => import('./pages/Clients/ClientDetail'));
const Suppliers = lazyWithRetry('suppliers', () => import('./pages/Suppliers'));
const SupplierDetail = lazyWithRetry('supplier-detail', () => import('./pages/Suppliers/SupplierDetail'));
const ThemeBuilder = lazyWithRetry('theme-builder', () => import('./pages/ThemeBuilder'));
const LaunchCenter = lazyWithRetry('launch-center', () => import('./pages/LaunchCenter'));
const ApiKeys = lazyWithRetry('api-keys', () => import('./pages/ApiKeys'));
const Revenue = lazyWithRetry('revenue', () => import('./pages/Revenue'));
const Team = lazyWithRetry('team', () => import('./pages/Team'));
const Webhooks = lazyWithRetry('webhooks', () => import('./pages/Webhooks'));
const Account = lazyWithRetry('account', () => import('./pages/Account'));
const NotFound = lazyWithRetry('not-found', () => import('./pages/NotFound'));

const AUTHORIZED_GROUPS = [AuthGroup.FACILITATORS, AuthGroup.PARTNERS];
const { ADMIN, DEVELOPER } = sdk.UserAccountPermissionGroup;

const router = createBrowserRouter([
  {
    path: '/sign-in',
    element: <SignIn />,
  },
  {
    path: '/sign-in-impersonate',
    element: <SignInImpersonate />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/sign-up',
    element: <SignUp />,
  },
  {
    path: '/unauthorized',
    element: <Unauthorized />,
  },
  {
    path: '/',
    element: <AuthGuard />,
    children: [
      {
        element: <RoleGuard requiredGroups={AUTHORIZED_GROUPS} />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              { index: true, element: <Overview /> },
              { path: 'clients', element: <Clients /> },
              { path: 'clients/:clientId', element: <ClientDetail /> },
              { path: 'suppliers', element: <Suppliers /> },
              { path: 'suppliers/:vendorId', element: <SupplierDetail /> },
              { path: 'vendors', element: <VendorsPage /> },
              { path: 'stats', element: <StatsPage /> },
              { path: 'embed-settings', element: <EmbedSettingsPage /> },
              { path: 'launch-status', element: <LaunchStatusPage /> },
              { path: 'demo', element: <DemoPage /> },
              { path: 'logs', element: <LogsPage /> },
              { path: 'help', element: <HelpPage /> },
              { element: <PermissionGuard requiredPermissions={[sdk.AuthPermission.FinanceRead]} />, children: [{ path: 'revenue', element: <Revenue /> }] },
              { path: 'account', element: <Account /> },  // "Account Settings"
              { element: <PermissionGuard requiredPermissions={[sdk.AuthPermission.UserAccountsRead]} />, children: [{ path: 'team', element: <Team /> }] },
              { element: <PermissionGuard requiredPermissions={[sdk.AuthPermission.ConfigRead]} />, children: [{ path: 'theme-builder', element: <ThemeBuilder /> }] },
              { path: 'launch-center', element: <LaunchCenter /> },
              { element: <PermissionGuard requiredPermissions={[sdk.AuthPermission.ApiKeysRead]} />, children: [{ path: 'api-keys', element: <ApiKeys /> }] },
              { element: <PermissionGuard requiredPermissions={[sdk.AuthPermission.WebhooksRead]} />, children: [{ path: 'webhooks', element: <Webhooks /> }] },
              { path: '*', element: <NotFound /> },
            ],
          },
        ],
      },
    ],
  },
]);

export default router;
